import { Add } from "@mui/icons-material";
import {
  Box,
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  Skeleton,
  Stack,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import AddNewCardForm from "components/add-new-card";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { completeBookingPayment } from "slices/bookingReducer";
import { fetchUserCards } from "slices/cardReducer";
import { handleSnackBar } from "slices/global";
import { useAppDispatch, useAppSelector } from "store/appHooks";
import { convertPrice } from "utils/currency.util";

const PayAmount = ({
  onClose,
  data,
  bookingId,
}: {
  onClose: any;
  data: any;
  bookingId: any;
}) => {
  const [selectedCard, setSelectedCard] = useState(0);
  const [addNewCard, setAddNewCard] = useState(false);
  const dispatch = useAppDispatch();
  const { cardReducer } = useAppSelector((state) => state);
  const { loadingCards } = cardReducer;
  const theme = useTheme();
  const matchesSm = useMediaQuery(theme.breakpoints.down("sm"));
  const matchesXs = useMediaQuery(theme.breakpoints.only("xs"));
  const navigate = useNavigate();

  useEffect(() => {
    dispatch(fetchUserCards({}));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const doPayment = () => {
    if (!selectedCard) {
      dispatch(
        handleSnackBar({
          open: true,
          message: "Please select card to make payment",
          severity: "error",
        })
      );
      return;
    }

    const payload = {
      id: bookingId,
      card_id: selectedCard,
    };

    dispatch(
      completeBookingPayment({
        data: payload,
        onSucess: () => navigate(`/booking-history/${bookingId}`),
      })
    );
  };

  return (
    <Dialog open onClose={onClose} fullScreen={matchesSm || matchesXs}>
      <DialogContent>
        <Stack gap="5px" mt="15px">
          <Typography
            sx={{
              color: "#323232",
              fontWeight: 700,
              fontSize: "26px",
              lineHeight: "31px",
            }}
          >
            Select Card
          </Typography>
          <Typography
            sx={{
              color: "#A2A2A2",
              fontWeight: 500,
              fontSize: "16px",
              lineHeight: "24px",
            }}
          >
            Choose a card from your saved cards for payment.
          </Typography>
        </Stack>
        <Stack
          gap="15px"
          mt="15px"
          sx={{
            maxHeight: {
              md: "400px",
              lg: "400px",
              xl: "400px",
              sx: "100px",
              sm: "100px",
            },
            minHeight: {
              md: "fit-content",
              lg: "fit-content",
              xl: "fit-content",
              sx: "100px",
              sm: "100px",
            },
            flexDirection: {
              md: "column",
              lg: "column",
              xl: "column",
              sm: "row",
              xs: "row",
            },
          }}
          overflow="auto"
          paddingBottom="10px"
        >
          {!loadingCards &&
            cardReducer.cards.length > 0 &&
            cardReducer.cards.map((card: any) => (
              <Box
                display="flex"
                gap="10px"
                justifyContent="flex-start"
                alignItems="flex-start"
              >
                <Checkbox
                  sx={{ padding: "0px", mt: "10px" }}
                  checkedIcon={
                    <img src="/assets/img/icons/radio_checked.svg" alt="" />
                  }
                  icon={
                    <img src="/assets/img/icons/radio_unchecked.svg" alt="" />
                  }
                  checked={selectedCard === card.id}
                  onClick={() => setSelectedCard(card.id)}
                />
                <Box
                  sx={{
                    background:
                      "url(/assets/img/icons/card_placeholder.svg) right bottom, url(/assets/img/icons/card_map.svg) left top ",
                    height: "176px",
                    width: "328px",
                    minWidth: "328px",
                    maxWidth: "328px",
                  }}
                  onClick={() => setSelectedCard(card.id)}
                >
                  <Box sx={{ padding: "20px" }}>
                    <Box
                      sx={{
                        display: "flex",
                        justifyContent: "space-between",
                      }}
                    >
                      <img
                        src="/assets/img/icons/visa_color.svg"
                        alt=""
                        width="28"
                        height="24"
                      />
                      <img
                        src="/assets/img/icons/card_chip.svg"
                        alt=""
                        width="28"
                        height="24"
                      />
                    </Box>
                    <Box
                      sx={{
                        mt: "15px",
                        display: "flex",
                        flexDirection: "column",
                        gap: "22px",
                      }}
                    >
                      <Typography
                        sx={{
                          color: "#FFFFFF",
                          fontSize: "18px",
                          fontWeight: 700,
                          lineHeight: "22px",
                          letterSpacing: "2px",
                          textShadow: "1px 2px #0000003D",
                        }}
                      >
                        XXXX XXXX XXXX {card.last_4}
                      </Typography>
                      <Typography
                        sx={{
                          color: "#FFFFFF",
                          fontSize: "13px",
                          fontWeight: 500,
                          lineHeight: "16px",
                          textShadow: "1px 2px #0000003D",
                        }}
                      >
                        {card.card_holder_name}
                      </Typography>
                      <Box
                        sx={{
                          display: "flex",
                          justifyContent: "space-between",
                          alignItems: "center",
                        }}
                      >
                        <Typography
                          sx={{
                            color: "#FFFFFF",
                            fontSize: "13px",
                            fontWeight: 500,
                            lineHeight: "16px",
                          }}
                        >
                          Valid thru: {card.expiry_month}/{card.expiry_year}
                        </Typography>
                        <Typography
                          sx={{
                            color: "#FFFFFF",
                            fontSize: "15px",
                            fontWeight: 700,
                            lineHeight: "18px",
                          }}
                        >
                          CVV
                        </Typography>
                      </Box>
                    </Box>
                  </Box>
                </Box>
              </Box>
            ))}

          {loadingCards && (
            <>
              <Box
                display="flex"
                gap="10px"
                justifyContent="flex-start"
                alignItems="flex-start"
              >
                <Checkbox
                  sx={{ padding: "0px", mt: "10px" }}
                  checkedIcon={
                    <img src="/assets/img/icons/radio_checked.svg" alt="" />
                  }
                  icon={
                    <img src="/assets/img/icons/radio_unchecked.svg" alt="" />
                  }
                  disabled
                />
                <Skeleton
                  variant="rectangular"
                  sx={{
                    height: "176px",
                    width: "328px",
                    minWidth: "328px",
                    maxWidth: "328px",
                    borderRadius: "10px",
                  }}
                />
              </Box>

              <Box
                display="flex"
                gap="10px"
                justifyContent="flex-start"
                alignItems="flex-start"
              >
                <Checkbox
                  sx={{ padding: "0px", mt: "10px" }}
                  checkedIcon={
                    <img src="/assets/img/icons/radio_checked.svg" alt="" />
                  }
                  icon={
                    <img src="/assets/img/icons/radio_unchecked.svg" alt="" />
                  }
                  disabled
                />
                <Skeleton
                  variant="rectangular"
                  sx={{
                    height: "176px",
                    width: "328px",
                    minWidth: "328px",
                    maxWidth: "328px",
                    borderRadius: "10px",
                  }}
                />
              </Box>
            </>
          )}
        </Stack>

        <Button
          startIcon={<Add />}
          sx={{ p: 0, mt: "10px" }}
          onClick={() => setAddNewCard(true)}
        >
          Add New Card
        </Button>
      </DialogContent>
      <DialogActions sx={{ px: "30px", pb: "20px" }}>
        <Button
          variant="outlined"
          sx={{ borderRadius: "10px", height: "56px" }}
          size="large"
          fullWidth
          onClick={onClose}
        >
          Cancel
        </Button>
        <Button
          variant="contained"
          sx={{ borderRadius: "10px", height: "56px" }}
          size="large"
          fullWidth
          onClick={doPayment}
        >
          Pay {convertPrice(data?.amount_due)}
        </Button>
      </DialogActions>

      {addNewCard && (
        <Dialog open sx={{ p: "0" }} onClose={() => setAddNewCard(false)}>
          <AddNewCardForm onSuccess={() => setAddNewCard(false)} />{" "}
        </Dialog>
      )}
    </Dialog>
  );
};

export default PayAmount;
